import { ComponentProps, memo, useCallback, useState } from 'react';
import classes from 'classnames';
import ReactSlider from 'react-slider';
import HandleIcon from '@/assets/icons/vertical-hamburger.svg?react';
import styles from './Slider.module.scss';
import { numberToLocale } from '@/utils/translate';

export type Props = {
  min: number;
  max: number;
  value?: number;
  heading?: string;
  step?: number;
  units?: string;
  onChange?: (value: number) => void;
  onAfterChange: (value: number) => void;
  dataTest?: string;
  className?: string;
  disabled?: boolean;
};

const Slider = memo(function Slider({
  min,
  max,
  value,
  heading,
  step,
  units,
  onChange,
  onAfterChange,
  dataTest = 'slider',
  className,
  disabled,
}: Props) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const showTooltip = useCallback(() => {
    setIsTooltipVisible(true);

    // Hide tooltip when mosue released anywhere on the page
    document.addEventListener(
      'mouseup',
      () => {
        setIsTooltipVisible(false);
      },
      { once: true },
    );
  }, []);

  const renderThumb = useCallback<
    NonNullable<ComponentProps<typeof ReactSlider>['renderThumb']>
  >(
    (props, state) => {
      const { valueNow } = state;
      // eslint-disable-next-line react/prop-types
      const { key, ...restProps } = props;

      return (
        <div key={key} {...restProps}>
          <div
            role="button"
            tabIndex={0}
            onMouseDown={showTooltip}
            className={styles.handle}
          >
            <HandleIcon className={styles['handle-icon']} />
            {isTooltipVisible && (
              <div className={classes(styles.tooltip)}>
                <div className={styles.amount}>
                  <span data-test="thumb" className={styles.value}>
                    {valueNow}
                  </span>
                  {units && <span className={styles.units}>{units}</span>}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    },
    [showTooltip, isTooltipVisible, units],
  );

  return (
    <div data-test={dataTest} className={className}>
      <div className={styles.labels}>
        <span className={styles.heading} data-test={`${dataTest}-heading`}>
          {heading}
        </span>
        <span
          className={styles['amount-label']}
          data-test={`${dataTest}-heading-value`}
        >
          {numberToLocale(value)}
          {units && <span className={styles.units}> {units}</span>}
        </span>
      </div>
      {!disabled && (
        <>
          <ReactSlider
            className={styles.slider}
            min={min}
            max={max}
            step={step}
            value={value}
            thumbClassName={styles.thumb}
            trackClassName={styles.track}
            renderThumb={renderThumb}
            onChange={onChange}
            onAfterChange={onAfterChange}
            data-test={`${dataTest}-slider`}
          />
          <div className={styles.limits}>
            <span data-test={`${dataTest}-min`}>
              {min} {units}
            </span>
            <span data-test={`${dataTest}-max`}>
              {max} {units}
            </span>
          </div>
        </>
      )}
    </div>
  );
});

export default Slider;
